import { Box, Button, Checkbox, CircularProgress, Dialog, FormControl, FormControlLabel, IconButton, TextField, Typography, useTheme } from '@mui/material'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import React from 'react'
import { useEffect } from 'react'
import { useState } from 'react'
import * as Yup from 'yup'
import api from '../../service/api'
import { toast } from 'react-toastify'
import { Icons } from '../../utils/utilities'
import Switch from '../common/CustomSwitch'

const controls = [
    { label: "Dashboard (Admin Dashboard)", permissionName: "analytics" },
    { label: "Academics (Manage academic rules)", permissionName: "academics" },
    { label: "Admission Exams", permissionName: "admission_exams" },
    { label: "Announcements", permissionName: "announcements" },
    { label: "API Settings", permissionName: "api_settings" },
    { label: "Attendance Audit", permissionName: "attendance_audit" },
    { label: "Auditing", permissionName: "auditing" },
    { label: "Control Panel", permissionName: "control_panel" },
    { label: "Debug User", permissionName: "debug_user" },
    { label: "Service Logs", permissionName: "debug_logs" },
    { label: "Discussions", permissionName: "discussions" },
    { label: "Exam Audit", permissionName: "exam_audit" },
    { label: "Feedback", permissionName: "feedback" },
    { label: "Live Class Audit", permissionName: "live_class_audit" },
    { label: "Online course", permissionName: "online_course" },
    { label: "Recordings", permissionName: "recordings" },
    { label: "Resume Builder", permissionName: "resume_builder" },
    { label: "Study materials", permissionName: "study_material_audit" },
    { label: "Impersonation", permissionName: "impersonation" },
]

const defaultPermissions = {}
const allPermissions = {}
for ( const permission of controls ) {
    defaultPermissions[permission.permissionName] = false
    allPermissions[permission.permissionName] = true
}

const AddEditAdmin = ( { setMode, mode, user } ) => {
    const [permissions, setPermissions] = useState( mode === "add" ? defaultPermissions : user.user_permissions || user.permissions )
    const [isIndeterminate, setIsIndeterminate] = useState( false )
    const [isAllChecked, setIsAllChecked] = useState( false )
    const [waiting, setWaiting] = useState( false )

    const { palette, border } = useTheme()


    const handleAllChange = ( e ) => {
        setIsAllChecked( e.target.checked )
        if ( e.target.checked ) {
            setPermissions( allPermissions )
        } else {
            setPermissions( defaultPermissions )
        }
    }

    const addNewAdmin = async ( values ) => {
        setWaiting( true )
        try {
            if ( values.password.trim() !== "" && values.password ) {
                const res = await api.createSubAdminAccount( { ...values, permissions } )
                toast( res.data.message )
                setMode( "manage" )
            }
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Something went wrong while adding new subadmin! please try again later" )
        } finally {
            setWaiting( false )
        }
    }

    const updateAdminUser = async ( values ) => {
        try {
            setWaiting( true )
            const res = await api.updateSubAdminAccount( { ...values, permissions }, user.user_id )
            toast( res.data.message )
            setMode( "manage" )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Something went wrong while updating new subadmin! please try again later" )
        } finally {
            setWaiting( false )
        }
    }

    const initialValues = {
        email: mode === "edit" ? user.user_email : "",
        username: mode === "edit" ? user.user_name : "",
        password: mode === "edit" ? "" : ""
    }

    const validationSchema = Yup.object().shape( {
        email: Yup.string().email( "Please provide valid email" ).required( "E-mail is required!" ),
        username: Yup.string().required( "Username is required!" ),
        // password: Yup.string().required( "Password is required!" )
        password: Yup.string().test( "TEST FOR PASSWORD", "Password is required!", ( val ) => {
            if ( mode === 'edit' ) return true
            return val?.match( `^(?=.*[A-Z])(?=.*[!@#$&*])(?=.*[0-9])(?=.*[a-z]).{8,32}$` )
        } )
    } )

    useEffect( () => {
        let previous = undefined
        let isDiff = false
        for ( const permission in permissions ) {
            if ( previous === true || previous === false ) {
                if ( previous !== permissions[permission] ) {
                    setIsIndeterminate( true )
                    isDiff = true
                    break
                }
            } else
                previous = permissions[permission]
        }
        if ( !isDiff ) {
            setIsIndeterminate( false )
            setIsAllChecked( previous )
        }
    }, [permissions] )

    return (
        <Dialog open={mode === "add" || mode === "edit"} onClose={() => { setMode( "manage" ); setPermissions( defaultPermissions ) }} >
            <Box borderRadius="5px" display="flex" flexDirection="column">
                <Box display="flex" position="sticky" top="0" zIndex="10" bgcolor={palette.background.paper} alignItems="center" justifyContent="space-between" padding="20px" borderBottom={border[1]}>
                    <Box>
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">{mode === "add" ? "Add new admin" : "Edit user details"}</Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary'>{mode === "add" ? "Fill in details and grant access to account module-wise." : "Update subadmin account details and permissions."}</Typography>
                    </Box>
                    <IconButton onClick={() => { setMode( "manage" ); setPermissions( defaultPermissions ) }} >
                        {Icons.default.CloseIcon}
                    </IconButton>
                </Box>
                <Box padding="20px" flexGrow={1} overflow="auto">
                    <Formik onSubmit={mode === "add" ? addNewAdmin : updateAdminUser} initialValues={initialValues} validationSchema={validationSchema}>
                        {( { values, errors } ) => {
                            return (
                                <Form autoComplete='off'>
                                    <FormControl fullWidth >
                                        <Typography variant='subtitle2' color={palette.form.label} >E-mail</Typography>
                                        <Field autoComplete='off' id="admin-email-input" size="small" as={TextField} name="email" variant='outlined' />
                                        <Typography variant='subtitle2' fontSize="12px" color="error.main" > <ErrorMessage name="email" /> </Typography>
                                    </FormControl>
                                    <FormControl fullWidth margin="normal">
                                        <Typography variant='subtitle2' color={palette.form.label} >Username</Typography>
                                        <Field autoComplete='off' id="admin-username-input" size="small" as={TextField} type='text' name="username" variant='outlined' />
                                        <Typography variant='subtitle2' fontSize="12px" color="error.main" > <ErrorMessage name="username" /> </Typography>
                                    </FormControl>
                                    <FormControl fullWidth margin="normal">
                                        <Typography variant='subtitle2' color={palette.form.label} >{mode !== 'add' ? "Set new password" : "Password"}</Typography>
                                        <Field autoComplete='off' id="admin-password-input" size="small" as={TextField} type="password" name="password" variant='outlined' />
                                        <Typography variant='subtitle2' fontSize="12px" color="error.main" > <ErrorMessage name="password" /> </Typography>
                                    </FormControl>
                                    <Box marginTop="20px">
                                        <Typography variant='subtitle2' gutterBottom display="block" color="secondary">Grant permissions</Typography>
                                        <Box border={border[1]} borderRadius="10px" display="flex" flexDirection="column" bgcolor={palette.contentBg}>
                                            <FormControlLabel label={<Typography component="span" fontSize="16px" pl="10px">Grant All Permissions</Typography>} labelPlacement='start' sx={{ justifyContent: "space-between", borderBottom: border[1], margin: "0", padding: "1px 5px" }} control={<Checkbox checked={isAllChecked} onChange={handleAllChange} indeterminate={isIndeterminate} />} />
                                            <Box maxHeight="350px" padding="0 10px" overflow="auto" className="custom-scrollbar" display="flex" flexDirection="column">
                                                {controls.map( ctrl => (
                                                    <FormControlLabel key={ctrl.label} label={<Typography component="span" fontSize="12px">{ctrl.label}</Typography>} sx={{ paddingY: "0", margin: "0", justifyContent: "space-between", padding: "1px 5px", borderBottom: border[1], borderWidth: "2px" }} labelPlacement='start' control={<Switch size="small" checked={permissions[ctrl.permissionName]} onChange={() => setPermissions( { ...permissions, [ctrl.permissionName]: !permissions[ctrl.permissionName] } )} />} />
                                                ) )}
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box display="flex" justifyContent="flex-end" marginTop="20px" gap="10px">
                                        <Button disabled={waiting} sx={{ textTransform: "capitalize" }} startIcon={Icons.default.CancelIcon} onClick={() => { setMode( "manage" ); setPermissions( defaultPermissions ) }} color='greyed' disableElevation>Cancel</Button>
                                        <Button disabled={waiting} sx={{ textTransform: "capitalize" }} startIcon={waiting ? <CircularProgress size={14} /> : mode === "add" ? Icons.default.AddIcon : Icons.default.CheckCircleIcon} type='submit' variant='contained' disableElevation>{mode === "edit" ? waiting ? 'Updating...' : "Update" : waiting ? "Adding..." : "Add admin"}</Button>
                                    </Box>
                                </Form>
                            )
                        }}
                    </Formik>
                </Box>
            </Box>
        </Dialog>
    )
}

export default AddEditAdmin
